// ============================================================
// SeoHelmet.jsx — Per-route <head> injector
// ============================================================
// Reads route metadata from AssetSpine (SEO / SEO_DEFAULTS) and
// writes <title>, <meta description>, canonical URL, Open Graph
// tags, and Twitter card tags into <head>. One instance sits in
// App.js (see <RouteSeo/>) and reacts to the current pathname.
//
// The baseline tags in public/index.html carry data-baseline="1"
// so that on first client render we strip them cleanly — Helmet
// then becomes the single source of truth for every variable
// meta tag. Immutable brand tags (og:site_name, og:type,
// og:locale, twitter:card) stay static for crawler visibility.
// ============================================================
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SEO, SEO_DEFAULTS } from "@/config/AssetSpine";

export default function SeoHelmet({ routeKey }) {
  const meta = SEO[routeKey] || {};
  const title       = meta.title       || SEO_DEFAULTS.SITE_NAME;
  const description = meta.description || SEO_DEFAULTS.DESCRIPTION;
  const canonical   = meta.canonical   || SEO_DEFAULTS.BASE_URL;
  const ogImage     = meta.ogImage     || SEO_DEFAULTS.OG_IMAGE;

  // Strip the static baseline tags once React mounts so Helmet's
  // per-route tags are the only ones in the DOM (prevents crawler
  // duplicates and browser preview confusion).
  useEffect(() => {
    document
      .querySelectorAll('[data-baseline="1"]')
      .forEach((node) => node.remove());
  }, []);

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title"       content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={canonical} />
      <meta property="og:image"       content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={ogImage} />
    </Helmet>
  );
}
